import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef } from "react";
import { tv } from "tailwind-variants";

import { cn } from "../../utils/cn";

import type { HTMLAttributes, ReactNode } from "react";
import type { VariantProps } from "tailwind-variants";

const spinnerVariants = tv({
  slots: {
    base: "inline-flex items-center justify-center",
    icon: "animate-spin text-blue",
    container: "relative",
    overlay: [
      "absolute",
      "inset-0",
      "z-10",
      "flex",
      "items-center",
      "justify-center",
      "bg-white/60",
      "dark:bg-gray-900/60",
    ],
    content: "transition-opacity",
    tipText: "ml-2 text-gray-600 dark:text-gray-400",
  },
  variants: {
    size: {
      sm: {
        icon: "text-sm",
        tipText: "text-xs",
      },
      default: {
        icon: "text-xl",
        tipText: "text-sm",
      },
      lg: {
        icon: "text-3xl",
        tipText: "text-base",
      },
    },
    spinning: {
      true: {
        content: "pointer-events-none opacity-50",
      },
      false: {
        content: "",
      },
    },
  },
  defaultVariants: {
    size: "default",
    spinning: true,
  },
});

export interface SpinnerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof spinnerVariants> {
  /**
   * Whether the spinner is currently spinning/visible.
   * @default true
   */
  spinning?: boolean;
  /**
   * Text to display next to the spinner.
   */
  tip?: ReactNode;
  /**
   * Content to wrap with the spinner overlay.
   * When provided with spinning=true, shows an overlay with the spinner on top of the content.
   */
  children?: ReactNode;
}

/**
 * A loading spinner component that can be used standalone or as an overlay on content.
 *
 * @example
 * // Standalone spinner
 * <Spinner />
 *
 * @example
 * // Spinner with tip text
 * <Spinner tip="Loading..." />
 *
 * @example
 * // Spinner wrapping content (overlay mode)
 * <Spinner spinning={isLoading}>
 *   <MyContent />
 * </Spinner>
 */
export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size, spinning = true, tip, children, ...props }, ref) => {
    const { base, icon, container, overlay, content, tipText } = spinnerVariants({
      size,
      spinning,
    });

    // Render just the spinner icon with optional tip
    const spinnerElement = (
      <div
        className={base({ className: children ? undefined : className })}
        {...(children ? {} : props)}
      >
        <FontAwesomeIcon icon={faSpinnerThird} className={icon()} />
        {tip && <span className={tipText()}>{tip}</span>}
      </div>
    );

    // If no children, render standalone spinner (or nothing if not spinning)
    if (!children) {
      if (!spinning) {
        return null;
      }
      return (
        <div ref={ref} className={base({ className })} {...props}>
          <FontAwesomeIcon icon={faSpinnerThird} className={icon()} />
          {tip && <span className={tipText()}>{tip}</span>}
        </div>
      );
    }

    // With children, render as overlay container
    return (
      <div ref={ref} className={cn(container(), className)} {...props}>
        {spinning && <div className={overlay()}>{spinnerElement}</div>}
        <div className={content()}>{children}</div>
      </div>
    );
  }
);
Spinner.displayName = "Spinner";
